/**
 * community/api.ts — the browser's client for the Community Hub (forum) backend (/api/community).
 *
 * Mirrors tools/api.ts: same-origin fetch, the session cookie is the only credential (no token in
 * the browser, ever), and every call is normalised to a small result union so loaders/actions can
 * branch without re-inspecting the Response. Unlike the Dataverse routes, the community family is
 * ALWAYS mounted (not feature-flagged), so a 404 here is a real not-found (a missing/forbidden
 * thread or channel) — never "feature disabled". We therefore use the no-feature result shape.
 *
 * The response interfaces below mirror the backend's SAFE shapes exactly (camelCase). The backend
 * derives the author from the session; the browser never sends an owner id.
 */

// ── Response shapes (mirror routes/community.ts + db/community.ts) ─────────────────────────────

/** A channel the caller may read, with whether they may also post (drives the composer's presence). */
export interface ChannelSummary {
  id: number;
  slug: string;
  name: string;
  canPost: boolean;
}

/** One thread row in a channel list or the Saved list. authorName null = departed-author marker. */
export interface ThreadSummary {
  threadId: string;
  channelId: number;
  title: string;
  authorName: string | null;
  // The starter's PUBLIC avatar, carried as kind+ref (+ directory-photo fallback) so the web
  // resolves it through the shared allowlist (avatar.ts) — never a raw URL. All null when the
  // author is the departed marker (authorName === null), matching PostNode's suppression (CH-F04).
  avatarKind: string | null;
  avatarBlobRef: string | null;
  photoBlobRef: string | null;
  createdAt: string;
  lastPostAt: string;
  replyCount: number;
  likeCount: number;
  acceptedPostId: string | null;
  solved: boolean;
}

/** A bounded, server-authorised cross-channel row. Restricted channels never enter this response. */
export interface CommunityFeedThread extends ThreadSummary {
  channelSlug: string;
  channelName: string;
  body: string;
  contentType: 'announcement' | 'event';
  eventStartsAt: string | null;
  eventLocation: string | null;
  eventJoinUrl: string | null;
}

export type CreateChampionAnnouncementInput =
  | { contentType: 'announcement'; title: string; body: string }
  | {
      contentType: 'event';
      title: string;
      body: string;
      eventStartsAt: string;
      eventLocation: string;
      eventJoinUrl: string | null;
    };

export type ChampionAnnouncement = CreateChampionAnnouncementInput & {
  threadId: string;
  createdAt: string;
  authorName: string;
};

export interface UpcomingChampionEvent {
  threadId: string;
  title: string;
  body: string;
  startsAt: string;
  location: string;
  joinUrl: string | null;
  authorName: string | null;
}

export interface CommunityFeedResponse {
  threads: CommunityFeedThread[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** An author's genuinely-held role markers (CH-F05), resolved by the backend at read time. */
export interface AuthorMarkers {
  champion: boolean;
  verified: boolean;
}

/** One post in a thread's tree. Children are oldest-first (conversational order, CH-F08). */
export interface PostNode {
  postId: string;
  parentPostId: string | null;
  depth: number;
  authorName: string | null; // null = departure marker (CH-F04)
  authorUserId: string | null;
  // The author's PUBLIC avatar, carried as kind+ref (+ directory photo fallback) so the web
  // resolves it through the shared allowlist (avatar.ts) — never a raw URL. All null on a removed
  // post (the backend suppresses them with the name/id/markers).
  avatarKind: string | null;
  avatarBlobRef: string | null;
  photoBlobRef: string | null;
  markers: AuthorMarkers;
  body: string; // "[removed]" when removed
  createdAt: string;
  edited: boolean; // CH-F10
  removed: boolean;
  likeCount: number;
  likedByViewer: boolean;
  isOwn: boolean; // the caller authored it (drives edit/delete/like affordances)
  children: PostNode[];
}

/** A whole thread: its own title+body, plus the nested tree of standalone top-level comments. */
export interface ThreadDetail {
  threadId: string;
  channelId: number;
  channelSlug: string;
  title: string;
  body: string; // the thread's own description — NOT a post; not reply/like/report-able
  // The thread starter's byline, resolved exactly like a post author's (same avatar allowlist,
  // same live role markers). authorName null = the departed-author marker (CH-F04).
  authorName: string | null;
  authorUserId: string | null;
  avatarKind: string | null;
  avatarBlobRef: string | null;
  photoBlobRef: string | null;
  markers: AuthorMarkers;
  isOwn: boolean;
  savedByViewer: boolean;
  acceptedPostId: string | null;
  posts: PostNode[]; // top-level = standalone comments; replies nest via `children`
}

/** One published forum rule (CH-F16). Ordered by `ordinal`; the plain list every channel links to. */
export interface ForumRule {
  ruleId: string;
  ordinal: number;
  text: string;
}

/**
 * One PUBLIC displayed badge on a colleague's profile (PR-F43), mirroring backend db/profile.ts
 * PublicBadge. Labels only — NEVER a count/total/score (PC-F31): `criterionText` is the masterlist's
 * own how-to-earn wording, shown as the chip's hover title.
 */
export interface PublicBadge {
  badgeKey: string;
  name: string;
  criterionText: string;
}

/**
 * One contribution figure a person has CHOSEN to publish (`UR-F21`/`PR-F27`/`PR-F56`), mirroring
 * backend `db/profile.ts` `PublicContributionFigure`.
 *
 * HAND-WRITTEN MIRROR, like every shape in this file: nothing generates it, so a rename on the server
 * compiles clean here and silently renders nothing. Proved against the wire, not the type — on the
 * seed, `GET /api/users/2` carries `{"figure":"total_adoptions","value":7}`.
 *
 * `figure` is one of a CLOSED set of six the server owns (0012's CHECK constraint). It is typed as a
 * plain string on purpose: a response is untrusted data, and re-declaring the union here would let a
 * client-side list of six drift from the server's — the renderer looks the name up in its own label
 * map and skips anything it does not recognise (see `CONTRIBUTION_FIGURE_LABEL` in Profile.tsx).
 *
 * AN UNCHOSEN FIGURE IS ABSENT from the array — there is no `shown` flag to render around, and
 * nothing says how many were withheld (`PR-F05`). `[]` is the normal answer for someone who shows
 * none, and the section is then omitted entirely rather than rendered empty (`PR-F31`).
 */
export interface PublicContributionFigure {
  figure: string;
  value: number;
}

/**
 * One completed training step a person has CHOSEN to publish (`PR-F30`), mirroring backend
 * `db/profile.ts` `PublicLearningStep`.
 *
 * DELIBERATELY THE WHOLE SERVER SHAPE — a step number, and nothing else. There is no `shown` flag,
 * because an unchosen step is ABSENT from the array rather than present and marked (`PR-F05`), and no
 * completion date, because a date would turn a list of finished lessons into an activity timeline (the
 * same reason a badge carries no `earned_at`). The step's WORDING is not here either: the server's own
 * step titles adapt to the reader's self-declared competency, so publishing one would disclose something
 * `UR-F18`/`PR-F12` let a person hide. The renderer names the step from `LEARNING_STEP_LABEL`
 * (Profile.tsx) and skips a number it cannot name.
 */
export interface PublicLearningStep {
  stepNo: number;
}

/**
 * One self-declared certification on a colleague's profile (`UR-F32`/`PR-F49`), mirroring the
 * `Certification` shape `db/profile.ts`'s public adapter builds — `ordinal` is the person's own 1..5
 * ordering, which is all the page uses it for.
 *
 * NOTHING HERE MAY IMPLY VERIFICATION: no issuer, no credential id, no expiry, no verified flag, not
 * even a field named for one. `PR-F49` is explicit that "nothing in the platform checks a certification,
 * and the profile must not imply otherwise", so the mirror carries exactly the two facts the person
 * typed and the page renders them under a heading that says so.
 */
export interface PublicProfileCertification {
  ordinal: number;
  name: string;
  year: number;
}

/**
 * A colleague's PUBLIC profile as GET /api/users/:userId returns it (backend db/profile.ts
 * PublicProfile). This is the §3.2-PUBLIC subset only — never the owner-private set. The avatar is
 * carried as kind+ref so the web resolves it through the shared allowlist (avatar.ts), never as a
 * raw URL. `self` is null when the person has no self-declared block OR has hidden it from others.
 * `badges` is the person's chosen displayed set — `[]` when they display none OR opted out (never a
 * count).
 *
 * THIS IS NOW THE WHOLE §3.2 SET, WHICH IT WAS NOT (`PR-F02`, completed 2026-08-27). A HAND-WRITTEN
 * MIRROR MISSING A FIELD DROPS IT SILENTLY, with both typechecks green and no runtime complaint — the
 * defect class this branch has already been bitten by three times (`MD-F29`, `MD-F06`, `CH-F04`). Three
 * fields the endpoint returns were absent here: `markers`, `certifications` inside `self`, and
 * `learningSteps`. `UserProfile.tsx` had been widening this type locally to reach them, on the belief
 * that this file belonged to another stream — it does not (`DEMO_WORKSTREAMS.md` puts `web/src/community/**`
 * in Stream C), and a local widening beside one consumer is a mirror only that consumer benefits from.
 * They are declared here now, so the NEXT page to read a public profile gets the whole set by default
 * instead of rediscovering what is missing.
 *
 * NO `status` FIELD, and it must not come back (`UR-F25a`) — see the comment on the field order below.
 */
export interface PublicProfile {
  userId: string;
  displayName: string;
  // NO `status` field. The endpoint stopped returning one when UR-F25a made a departed colleague's
  // profile unreadable — every profile that resolves at all is now Active, so a status here would be
  // a constant, and a departed person is indistinguishable from an unknown id (a 404) by design.
  memberSince: string; // ISO
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  photoBlobRef: string | null; // directory photo (public directory field) — resolveAvatar's fallback
  avatarKind: string | null;
  avatarBlobRef: string | null;
  // Team and role label are DISPLAY ONLY (UR-F34) and community-label sourced, NOT part of the
  // self-declared block — so they are top-level and returned unconditionally, never hidden by
  // self_section_hidden. They read by nothing else: not a filter, metric, ordering or search key.
  team: string | null;
  roleLabel: string | null;
  /**
   * Role markers (`PR-F24`) — Champion and SWAT ONLY, resolved by the server from a LIVE, non-revoked
   * appointment (`UR-F08`/`UR-NFR02`) rather than from anything stored on the person. Reviewer, Manager
   * and PlatformOwner are access roles and never appear: surfacing one would disclose who can approve
   * what. `[]` where the person holds neither, and the page then renders no marker list at all.
   *
   * A MARKER IS A LABEL A PERSON HOLDS, NEVER A FIGURE (`PC-F31`): nothing counts them, orders by them
   * or compares two people through them. A person also cannot give themselves one, and a self-assessed
   * competency level is never a marker — that lives inside `self` and is labelled "self-assessed".
   */
  markers: ('Champion' | 'SWAT')[];
  self: {
    bio: string | null;
    interests: string[];
    skillsHave: string[];
    skillsWanted: string[];
    // Competency (self-assessed, PR-F25) and maker type (self-declared) are part of the self block,
    // so they live INSIDE `self` and vanish with it when the owner hides their self section.
    competencyLevel: string | null;
    makerType: string | null;
    /**
     * Self-declared certifications (`UR-F32`/`PR-F49`), in the person's own 1..5 order. INSIDE `self`
     * because `PR-F12` names certifications in the set that hiding covers, so they leave with the rest
     * of the block rather than surviving it as a section of their own.
     */
    certifications: PublicProfileCertification[];
  } | null;
  badges: PublicBadge[];
  /**
   * The contribution figures the person CHOSE to show, from the closed six (`UR-F21`/`PR-F27`/
   * `PR-F56`) — three by default, the rest by their choice (`UR-F33`). Optional here because a
   * response is untrusted data: an API that omits it must leave the page showing no summary at all
   * rather than throwing on `undefined.map`, the same reason `followerCount` is optional on the
   * people mirror.
   */
  contributionFigures?: PublicContributionFigure[];
  /**
   * The completed training steps the person CHOSE to show (`PR-F30`), ascending. Optional for the same
   * untrusted-response reason as the figures above.
   *
   * `[]` IS THE NORMAL ANSWER, not an edge case: every step defaults to HIDDEN, which is the inverse of
   * the contribution summary's default of three shown, because `PR-F30` says "nothing learning-related is
   * shown publicly unless they have chosen to show it". The server also joins each choice through an
   * actual completion, so a step in this array is always one the person really finished — a display
   * preference cannot assert training that did not happen. The page omits the section when it is empty
   * (`PR-F31`), and never prints a count or an "n of 7" (`PC-F31`): that arithmetic would republish
   * exactly what the person's per-step choices withheld.
   */
  learningSteps?: PublicLearningStep[];
}

/** One of a person's PUBLISHED tools on their public profile (PR-F29). SAFE fields only. */
export interface PublicProfileTool {
  toolId: string;
  name: string;
  publicationStatus: string;
  healthState: string;
  lastUpdated: string | null;
  /**
   * The tool's encoded flow TOPOLOGY — the string `FlowArt` decodes to draw the diagram on this
   * person's tool panels. OPAQUE HERE: nothing outside FlowArt reads it, and nothing else should
   * start to. `null` when the definition could not be walked, which draws the identity emblem
   * instead of inventing a shape.
   *
   * The same string, for these same Approved tools, already reaches any signed-in viewer through the
   * catalogue's own cards (`CatalogueToolSummary.flowShape`), so carrying it here discloses nothing
   * new — see the note on the route that fills it in (api/src/http/routes/users.ts).
   */
  flowShape: string | null;
}

/**
 * The whole GET /api/users/:userId body: the public profile plus their published tools, and the
 * follow relationship (PR-F51/F52). `followerCount` is a bare public number — how many people follow
 * this person, NEVER who they are (PR-F52). `isSelf` = you are viewing your own profile via this
 * route (no follow button then). `viewerFollows` tells YOU only whether YOU follow this person, to set
 * the follow button's state; it is omitted when `isSelf` (you cannot follow yourself).
 */
export interface PublicProfileResponse {
  profile: PublicProfile;
  tools: PublicProfileTool[];
  followerCount: number;
  isSelf: boolean;
  viewerFollows?: boolean;
}

/** One person in the owner's private "who I follow" list (GET /api/me/following, backend FollowingEntry). */
export interface FollowingEntry {
  userId: string;
  displayName: string;
  avatarKind: string | null; // 'stock' | 'upload' | null — how resolveAvatar reads the ref
  avatarBlobRef: string | null; // presentation override; null → fall back to the directory photo
  photoBlobRef: string | null; // directory photo (resolveAvatar's fallback)
}

/** One newly-published tool in the home feed (GET /api/me/feed, backend FeedToolSummary). */
export interface FeedTool {
  toolId: string;
  name: string;
  builderUserId: string; // the followed person — links to their public profile
  builderName: string; // snapshot name (survives departure)
  datePublished: string | null; // ISO
}

/**
 * PUT/DELETE /api/me/following/:userId — follow or unfollow a colleague (PR-F51). The FOLLOWER is
 * always the session user (the backend derives it; no id is sent). Idempotent: both return 204. A
 * 400 cannot_follow_self is possible on PUT if the id is your own (the UI hides the button for self,
 * so this is only a backstop). Returns whether the write succeeded so the caller can revalidate.
 */
export async function putFollow(userId: string): Promise<boolean> {
  const res = await fetch(`/api/me/following/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.ok;
}

export async function deleteFollow(userId: string): Promise<boolean> {
  const res = await fetch(`/api/me/following/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.ok;
}

// ── Fetch helpers (same-origin, session cookie, JSON) ──────────────────────────────────────────

/**
 * A call's outcome. `ok` carries the parsed body; `unauthorized` is the signed-out (401) case;
 * `error` is any other non-OK, with the status and the backend's `error` code (e.g. 'thread_not_found',
 * 'channel_forbidden', 'cannot_like_own_post'). No feature-disabled arm — the family is always on.
 */
export type ApiResult<T> =
  | { kind: 'ok'; body: T }
  | { kind: 'unauthorized' }
  | { kind: 'error'; status: number; code: string | null; detail?: unknown };

/** Parse a JSON body defensively; a non-JSON error body becomes null rather than throwing. */
async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Normalise a completed fetch Response into the result union. Shared by every verb below.
 *
 * A success does NOT imply a body. Several of our writes answer 204 No Content — the
 * self-assessment and link-open records (routes/learning.ts), the notification mark-read pair
 * (routes/notifications.ts) — which is why those callers declare `<null>`/`<never>`. Calling
 * `res.json()` on an empty body throws SyntaxError, and that rejection escapes the verb helpers
 * and strands the caller: an awaited `save()` never reaches its own `setSaving(false)`, so the UI
 * sits on "Saving…" showing no error even though the write succeeded. So a bodyless success is
 * reported as `body: null`, and — for the same reason — a malformed body under an OK status
 * becomes an `error` result rather than a throw. This function must never reject.
 */
async function normalise<T>(res: Response): Promise<ApiResult<T>> {
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.ok) {
    // 204 and 205 carry no body by definition; an explicit zero length says so for any status.
    if (res.status === 204 || res.status === 205 || res.headers.get('Content-Length') === '0') {
      return { kind: 'ok', body: null as T };
    }
    // Read as text first so an empty body is distinguishable from a malformed one.
    const text = await res.text();
    if (text.trim().length === 0) return { kind: 'ok', body: null as T };
    try {
      return { kind: 'ok', body: JSON.parse(text) as T };
    } catch {
      return { kind: 'error', status: res.status, code: 'malformed_response' };
    }
  }
  const body = (await readJson(res)) as { error?: string; detail?: unknown } | null;
  return { kind: 'error', status: res.status, code: body?.error ?? null, detail: body?.detail };
}

/** GET a community route. */
export async function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return normalise<T>(
    await fetch(path, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
  );
}

/**
 * POST to a community route. The body is a plain object we control — never a token or owner id.
 * Many community POSTs carry no body at all (a like, a save): those send no Content-Type and no
 * body, rather than an empty string, so the init object stays valid under exactOptionalPropertyTypes.
 */
export async function apiPost<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
  const init: RequestInit =
    body === undefined
      ? { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' }
      : {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        };
  return normalise<T>(await fetch(path, init));
}

/** PATCH JSON (edit a post). */
export async function apiPatch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }),
  );
}

/**
 * PUT JSON — the idempotent whole-value write. Identical wire handling to apiPatch; the verb is what
 * differs, and it differs for two reasons.
 *
 * Some admin records have no partial form: recording a workforce headcount for a scope REPLACES
 * whatever statement was there, so a repeated request from a double-click cannot accumulate anything.
 *
 * And the server distinguishes the verbs on one router: PUT /api/admin/settings/headcount records the
 * headcount for one scope (MD-F04), while PATCH on that router means "amend the setting named by :key".
 * A PATCH to /headcount would fall through to that handler and look for a setting called "headcount".
 *
 * So: PUT where a second identical call must leave the same state, PATCH where the request names a
 * field of a larger row.
 */
export async function apiPut<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }),
  );
}

/** DELETE a community resource (a post, a like, a save). */
export async function apiDelete<T>(path: string): Promise<ApiResult<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
  );
}
